import React, { PureComponent } from 'react';
import {
    FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    FlightDto,
    FlightListByTvRequestDto,
    TimePeriod,
    TimeWindowType,
    TrafficType,
} from '../../../../model/CORE';
import { RadRestriction } from '../../../../model/SOL49';
import FlightTable, { FLIGHT_SELECTED_COLUMN, FlightColumn, StandardFlightColumn } from '../../../table/FlightTable';
import AppContext from '../../../../common/context/AppContext';
import { ServiceFactory } from '../../../../service/ServiceFactory';
import { notification } from 'antd';
import { Flex } from '../../../common/Flex';
import KeyUtils from '../../../../common/util/KeyUtils';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION, MultipleSelection } from '../../../../common/table/ManagedTable';

interface RadInformationPanelProps {
    selectedTimePeriod: TimePeriod;
    selectedRadRestriction: RadRestriction;
    onFLightsSelected: (flights: Array<FlightDto>) => void;
}

interface RadInformationPanelState {
    flightList: FlightDto[];
    selectedFlights: FlightDto[];
    isFlightListLoading: boolean;
}

export class RadInformationPanel extends PureComponent<RadInformationPanelProps, RadInformationPanelState> {
    constructor(props: RadInformationPanelProps) {
        super(props);
        this.state = {
            flightList: [],
            selectedFlights: [],
            isFlightListLoading: true,
        };
    }

    componentDidMount() {
        this.requestRadRestrictionFlightList(this.props.selectedRadRestriction);
    }

    componentDidUpdate(prevProps: Readonly<RadInformationPanelProps>) {
        if (
            prevProps.selectedRadRestriction?.id !== this.props.selectedRadRestriction.id ||
            prevProps.selectedTimePeriod !== this.props.selectedTimePeriod
        ) {
            this.requestRadRestrictionFlightList(this.props.selectedRadRestriction);
        }
    }

    SIMPLE_FLIGHT_COLUMNS: FlightColumn[] = [
        FLIGHT_SELECTED_COLUMN,
        StandardFlightColumn.ENTRY_TIME,
        StandardFlightColumn.EXIT_TIME,
        StandardFlightColumn.ARCID,
        StandardFlightColumn.ATYPE,
        StandardFlightColumn.ADEP,
        StandardFlightColumn.ADES,
        StandardFlightColumn.CFMU_TYPE,
        StandardFlightColumn.ATOT_TTOT,
        StandardFlightColumn.CTOT,
        StandardFlightColumn.ATOT,
        StandardFlightColumn.xTA,
        StandardFlightColumn.MPR,
        StandardFlightColumn.DELAY,
        StandardFlightColumn.CONVECTION_IMPACT,
    ];

    private requestRadRestrictionFlightList(radRestriction: RadRestriction) {
        this.setState({
            flightList: [],
            selectedFlights: [],
            isFlightListLoading: true,
        });
        const flightListRequest: FlightListByTvRequestDto = {
            clazz: FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            fields: FlightColumn.computeB2BFieldList(this.SIMPLE_FLIGHT_COLUMNS),
            dataset: AppContext.getDataset(),
            timeType: TimeWindowType.OCCUPANCY,
            tvName: radRestriction.id,
            trafficType: TrafficType.LOAD,
            trafficWindow: this.props.selectedTimePeriod,
        };
        if (this.props.selectedRadRestriction.preAgreed) {
            ServiceFactory.coreService()
                .getFlightList(flightListRequest)
                .then(reply => {
                    if (reply.error !== undefined) {
                        throw new Error(reply.error.message);
                    } else {
                        this.setState({
                            flightList: reply.data.flights,
                            isFlightListLoading: false,
                        });
                    }
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        }
    }

    private flightSelections = memoizeOne(
        (selection): MultipleSelection<FlightDto> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.props.onFLightsSelected,
            selectedItems: selection,
        }),
    );

    render() {
        const flightListToDisplay = this.state.flightList;
        const base64Png = this.props.selectedRadRestriction.imageBase64
            ? 'data:image/png;base64,' + this.props.selectedRadRestriction.imageBase64
            : null;
        return (
            <Flex direction="vertical" className={'applicable-rad-restrictions-scenarios-sub-panel'}>
                <Flex direction={'horizontal'} grow={false}>
                    <Flex
                        size="2em"
                        style={{ fontSize: '1.5em', fontWeight: 'bold', padding: '10px 0 10px 10px' }}
                        grow={false}>
                        {this.props.selectedRadRestriction.id}
                    </Flex>
                </Flex>
                <div className={'rad-description'}>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'rad-description-title'}>Publication Status</Flex>
                        <Flex
                            style={{
                                flex: '80%',
                                flexDirection: 'row',
                            }}>
                            {this.props.selectedRadRestriction.preAgreed ? 'Pre-agreed' : 'Draft'}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'rad-description-title'}>Applicability Period</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedRadRestriction.timeApplicability}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'rad-description-title'}>pointOrAirspaceBlack</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedRadRestriction.pointOrAirspaceBlack}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'rad-description-title'}>pointOrAirspaceRed</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedRadRestriction.pointOrAirspaceRed}
                        </Flex>
                    </Flex>
                </div>
                {this.props.selectedRadRestriction.preAgreed && (
                    <Flex style={{ borderTop: 'solid 1px #8C8B82', flexDirection: 'column', padding: '.5em' }}>
                        {base64Png && (
                            <Flex style={{ width: '100%' }}>
                                <img
                                    src={base64Png}
                                    alt="RAD image"
                                    style={{ maxWidth: '100%', maxHeight: '100%', height: 'auto', width: 'auto' }}
                                />
                            </Flex>
                        )}
                        <Flex grow={true}>
                            <FlightTable
                                data={flightListToDisplay}
                                columns={this.SIMPLE_FLIGHT_COLUMNS}
                                keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                                loading={this.state.isFlightListLoading}
                                selection={this.flightSelections(this.state.selectedFlights)}
                                noDataMessage={'No flights captured in this time period'}
                            />
                        </Flex>
                    </Flex>
                )}
            </Flex>
        );
    }
}
